
import { defineComponent, computed, PropType } from "vue";
import { Radar as RadarChart } from "vue-chartjs";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartData,
} from "chart.js";
import { GroupedAlert } from "@/stores/telegramAlerts";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

export default defineComponent({
  name: "OddsRadar",
  components: {
    RadarChart,
  },
  props: {
    alerts: {
      type: Array as PropType<GroupedAlert[]>,
      required: true,
    },
  },
  setup(props) {
    // Custom plugin to draw labels
    const customDataLabels = {
      id: "customDataLabels",
      afterDatasetsDraw(chart: any) {
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset: any, i: number) => {
          const meta = chart.getDatasetMeta(i);
          meta.data.forEach((element: any, index: number) => {
            const value = dataset.data[index];
            if (value !== 0) {
              const { x, y } = element.getProps(["x", "y"], true);
              ctx.save();
              ctx.fillStyle = "#333";
              ctx.font = "bold 11px Arial";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              // Add simple outline for readability
              ctx.shadowColor = "white";
              ctx.shadowBlur = 4;
              ctx.shadowOffsetX = 0;
              ctx.shadowOffsetY = 0;
              ctx.fillText(value, x, y - 10); // Draw slightly above
              ctx.restore();
            }
          });
        });
      },
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: 5, // Add padding for labels
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              return `Alertas: ${context.parsed.r}`;
            },
          },
        },
      },
      scales: {
        r: {
          angleLines: {
            display: true,
            color: "rgba(0, 0, 0, 0.1)",
          },
          grid: {
            color: "rgba(0, 0, 0, 0.05)",
          },
          ticks: {
            display: false, // Hide numeric ticks to look cleaner
            backdropColor: "transparent",
          },
          pointLabels: {
            font: {
              size: 11,
            },
          },
          suggestedMin: 0,
        },
      },
    };

    const plugins = [customDataLabels];

    const chartData = computed((): ChartData<"radar", number[], string> => {
      // Define buckets
      const buckets = [
        { label: "< 1.30", min: 0, max: 1.3 },
        { label: "1.30 - 1.50", min: 1.3, max: 1.5 },
        { label: "1.50 - 1.70", min: 1.5, max: 1.7 },
        { label: "1.70 - 2.00", min: 1.7, max: 2.0 },
        { label: "2.00 - 3.00", min: 2.0, max: 3.0 },
        { label: "> 3.00", min: 3.0, max: 99999 },
      ];

      const counts = new Array(buckets.length).fill(0);

      props.alerts.forEach((alert) => {
        let oddsValue = 0;
        // Parse odds safely
        const rawOdds = alert.odds as unknown;
        if (typeof rawOdds === "number") {
          oddsValue = rawOdds;
        } else if (typeof rawOdds === "string") {
          const parsed = parseFloat(rawOdds.replace(",", "."));
          if (!isNaN(parsed)) {
            oddsValue = parsed;
          }
        }

        if (oddsValue > 0) {
          // Find bucket
          const index = buckets.findIndex(
            (b) => oddsValue >= b.min && oddsValue < b.max
          );
          if (index !== -1) {
            counts[index]++;
          }
        }
      });

      return {
        labels: buckets.map((b) => b.label),
        datasets: [
          {
            label: "Frecuencia",
            data: counts,
            backgroundColor: "rgba(94, 114, 228, 0.2)", // Primary Blue transparent
            borderColor: "rgba(94, 114, 228, 1)", // Primary Blue solid
            borderWidth: 2,
            pointBackgroundColor: "rgba(94, 114, 228, 1)",
          },
        ],
      };
    });

    return {
      chartData,
      chartOptions,
      plugins,
    };
  },
});
